import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Spa } from '@mui/icons-material';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import WellbeingCheckinModal from 'components/wellbeing/WellbeingCheckinModal';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import { PENDING_WELLBEING_CHECKIN } from 'api/graphql/wellbeing';

/**
 * Bienestar Corporativo — card del check-in en el Inicio. Solo aparece si el usuario es
 * miembro de una organización real Y tiene al menos un slot pendiente de esta semana: cero
 * ruido para el resto (self-service o ya al día).
 *
 * Cadencia: 3 check-ins por semana (lunes/miércoles/viernes) de UNA pregunta. Los slots que
 * ya abrieron y no se respondieron esperan al usuario dentro de la misma semana, así que aquí
 * pueden llegar 1, 2 o 3 preguntas y el texto tiene que decir cuántas son de verdad.
 */
function WellbeingCheckinCard() {
  const { t } = useTranslation(['wellbeing']);
  const [open, setOpen] = useState(false);

  const { data, loading } = useQuery(PENDING_WELLBEING_CHECKIN, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });

  const pending = data?.pendingWellbeingCheckin || null;
  const slots = pending?.slots || [];
  const count = slots.length;

  // Mientras no llegue la respuesta no sabemos si el usuario pertenece a una organización, y
  // la mayoría NO pertenece: un skeleton aquí sería ruido permanente en el Inicio de gente que
  // nunca verá esta card. Si la query falla, `pending` queda nulo y la card simplemente no
  // aparece (el check-in no es camino crítico; el detalle técnico va al errorHandler global).
  if (loading && !pending) return null;

  const hasPending = Boolean(pending?.eligible) && !pending.completed && count > 0;

  // El modal se mantiene montado mientras esté abierto aunque la card ya no aplique: al
  // enviar, el refetch marca la semana como completada y sin esto la pantalla de "gracias"
  // (con la práctica recomendada) se desmontaría en la cara del usuario.
  if (!hasPending && !open) return null;

  return (
    <>
      {hasPending && (
        <PremiumCard sx={{ mb: 3 }} padding={{ xs: 2.5, md: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <Box sx={{ position: 'relative', flex: 'none' }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'gold.main',
                  backgroundColor: 'gold.soft',
                  border: '1px solid',
                  borderColor: 'hairline.main'
                }}>
                <Spa />
              </Box>
              {count > 1 && (
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    minWidth: 20,
                    height: 20,
                    px: 0.5,
                    borderRadius: 999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 800,
                    fontVariantNumeric: 'tabular-nums',
                    // Badge en dorado SUAVE (no relleno): `gold.main` cambia de claro a
                    // profundo según el modo y no hay un color de texto que contraste con ambos.
                    color: 'gold.main',
                    backgroundColor: 'surface.content',
                    border: '1px solid',
                    borderColor: 'gold.main'
                  }}>
                  {count}
                </Box>
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                {t('cardTitle', { ns: 'wellbeing', defaultValue: '¿Cómo vas esta semana?' })}
              </Typography>
              <Typography variant="caption" component="p" sx={{ color: 'text.secondary' }}>
                {t('cardSubtitle', {
                  ns: 'wellbeing',
                  count,
                  defaultValue: '{{count}} preguntas rápidas. Anónimo para tu empresa.',
                  defaultValue_one: '1 pregunta, 10 segundos. Anónimo para tu empresa.'
                })}
              </Typography>
              {/* Sin esta línea, quien entra el viernes y ve 3 preguntas cree que la app se
                  descuadró o que le están preguntando de más. */}
              {count > 1 && (
                <Typography variant="caption" component="p" sx={{ color: 'text.disabled', mt: 0.25 }}>
                  {t('catchUpNote', {
                    ns: 'wellbeing',
                    defaultValue: 'Son las de esta semana que quedaron sin responder.'
                  })}
                </Typography>
              )}
            </Box>

            <Button onClick={() => setOpen(true)} sx={{ ...premiumButtonSx, flex: 'none', px: 3 }}>
              {t('cardCta', { ns: 'wellbeing', defaultValue: 'Responder' })}
            </Button>
          </Stack>
        </PremiumCard>
      )}

      <WellbeingCheckinModal open={open} pending={pending} onClose={() => setOpen(false)} />
    </>
  );
}

export default WellbeingCheckinCard;
